import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  UNRECORDED_EMPLOYEE_ID,
  type PaymentsByEmployeeRowDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { ReportRangePicker } from './ReportRangePicker';
import {
  matchPreset,
  rangeFromBusinessDay,
  rangeFromPreset,
  usePaymentsByEmployee,
  useReportCatalogue,
  type ReportPreset,
  type ReportRangeState,
} from './useReports';

/** Where this report lives — exported so the Reports list, App.tsx and the tests agree. */
export const PAYMENTS_BY_EMPLOYEE_PATH = '/reports/payments-by-employee';

/** One person's money across every method, signed: what passed through their hands. */
export function rowTotalMd(row: PaymentsByEmployeeRowDto): bigint {
  return BigInt(row.cashMd) + BigInt(row.accountBalanceMd) + BigInt(row.electronicMd);
}

export interface PaymentsByEmployeeTotals {
  cashMd: bigint;
  accountBalanceMd: bigint;
  electronicMd: bigint;
  totalMd: bigint;
  count: number;
}

/** The footer: every row added up in BigInt — never a float. */
export function paymentsByEmployeeTotals(
  rows: readonly PaymentsByEmployeeRowDto[],
): PaymentsByEmployeeTotals {
  return rows.reduce<PaymentsByEmployeeTotals>(
    (sum, row) => ({
      cashMd: sum.cashMd + BigInt(row.cashMd),
      accountBalanceMd: sum.accountBalanceMd + BigInt(row.accountBalanceMd),
      electronicMd: sum.electronicMd + BigInt(row.electronicMd),
      totalMd: sum.totalMd + rowTotalMd(row),
      count: sum.count + row.count,
    }),
    { cashMd: 0n, accountBalanceMd: 0n, electronicMd: 0n, totalMd: 0n, count: 0 },
  );
}

/**
 * Payments taken by employee (owner, 2026-10-12).
 *
 * Who pressed Pay, split by how the customer paid: cash, customer balance and
 * electronic (FIB, Qi…). It answers the "who took it" that the per-employee
 * boxes used to answer; the cash drawer itself counts everyone's cash together.
 *
 * The window is the same From/To every report takes, but the server reads it
 * against WHEN each payment was taken, not against business days: a checkout
 * carries the business day of the sitting it settles, which can be the day
 * before, and the question here is whose hands the money passed through.
 * Every figure is signed server-side (`isRefund ? −amount : amount`), so a
 * balance paid out or cash handed back counts against whoever handed it back.
 * The kiosks' own settlements — the built-in Portal actor — read as
 * "Self-service (PCs)": customers spending their balance, never cash.
 */
export function PaymentsByEmployeePage() {
  const { t } = useTranslation();
  const { can, venue } = useAuth();
  const navigate = useNavigate();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  // The API asks for the same key.
  const allowed = can('reports.view');
  const { catalogue, error: catalogueError } = useReportCatalogue();

  const [draftRange, setDraftRange] = useState<ReportRangeState | null>(null);
  const [appliedRange, setAppliedRange] = useState<ReportRangeState | null>(null);

  // Seed both ranges from the venue's own business day once it is known.
  useEffect(() => {
    if (!catalogue || draftRange) return;
    const seeded = rangeFromBusinessDay(
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
    );
    setDraftRange(seeded);
    setAppliedRange(seeded);
  }, [catalogue, draftRange]);

  const { rows, loading, error, reload } = usePaymentsByEmployee(allowed ? appliedRange : null);

  const applyPreset = (preset: ReportPreset) => {
    if (!catalogue) return;
    const next = rangeFromPreset(
      preset,
      catalogue.range.fromBusinessDate,
      catalogue.range.openingTime,
      catalogue.range.closingTime,
      catalogue.earliestBusinessDate,
    );
    setDraftRange(next);
    setAppliedRange(next);
  };

  // Fetch always fetches: pressed again on the same window it re-reads, so a
  // payment taken a minute ago shows without touching the range.
  const fetchNow = () => {
    if (!draftRange) return;
    if (
      appliedRange &&
      draftRange.from === appliedRange.from &&
      draftRange.to === appliedRange.to
    ) {
      void reload();
    } else {
      setAppliedRange(draftRange);
    }
  };

  if (!allowed) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-semibold text-content">{t('reports.title')}</h1>
        <p className="mt-2 text-sm text-content-muted">{t('reports.noViewPermission')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate('/reports')}
          title={t('reports.backToReports')}
          aria-label={t('reports.backToReports')}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-subtle text-content-secondary transition-colors hover:bg-hover hover:text-content"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
        </button>
        <h1 className="text-xl font-semibold text-content">
          {t('reports.paymentsByEmployee.title')}
        </h1>
      </div>
      <p className="px-4 pt-2 text-xs text-content-muted">
        {t('reports.paymentsByEmployee.intro')}
      </p>

      {draftRange ? (
        <div className="px-4 pt-3">
          <ReportRangePicker
            value={draftRange}
            onChange={setDraftRange}
            onFetch={fetchNow}
            busy={loading}
            onPreset={catalogue ? applyPreset : undefined}
            activePreset={catalogue ? matchPreset(draftRange, catalogue) : null}
          />
        </div>
      ) : null}

      {catalogueError ? (
        <p role="alert" className="px-4 pt-3 text-sm text-danger">
          {catalogueError}
        </p>
      ) : null}

      <div className="min-h-0 flex-1 px-4 pb-6 pt-4">
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : rows ? (
          <PaymentsByEmployeeTable rows={rows} currencySymbol={currencySymbol} />
        ) : (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        )}
      </div>
    </div>
  );
}

/**
 * The sheet. Exported so its rules are pinned without fetching: the Portal
 * actor reads as self-service and payments nobody signed for as "Not
 * recorded" (both worded here, never taken from the server's English), every
 * figure is signed, each person's total spans all three methods, and the
 * footer adds up exactly the rows shown.
 */
export function PaymentsByEmployeeTable({
  rows,
  currencySymbol,
}: {
  rows: readonly PaymentsByEmployeeRowDto[];
  currencySymbol: string;
}) {
  const { t, i18n } = useTranslation();
  const money = (md: bigint | string) =>
    formatIqd(fromMillidinars(md), { currencySymbol, locale: i18n.language });
  const nameOf = (row: PaymentsByEmployeeRowDto): string =>
    row.employeeId === UNRECORDED_EMPLOYEE_ID
      ? t('reports.paymentsByEmployee.notRecorded')
      : row.isSelfService
        ? t('reports.paymentsByEmployee.selfService')
        : row.name;

  if (rows.length === 0) {
    return <p className="text-sm text-content-muted">{t('reports.paymentsByEmployee.empty')}</p>;
  }

  const totals = paymentsByEmployeeTotals(rows);

  return (
    <>
      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full text-sm">
          <thead className="bg-overlay text-[10px] uppercase tracking-[0.14em] text-content-muted">
            <tr>
              <Th>{t('reports.paymentsByEmployee.employee')}</Th>
              <Th align="end">{t('payments.cash')}</Th>
              <Th align="end">{t('payments.accountBalance')}</Th>
              <Th align="end">{t('payments.electronic')}</Th>
              <Th align="end">{t('common.total')}</Th>
              <Th align="end">{t('reports.paymentsByEmployee.count')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.employeeId}
                data-employee-row={row.employeeId}
                className="border-t border-subtle"
              >
                <Td>{nameOf(row)}</Td>
                <Td align="end" strong>
                  {money(row.cashMd)}
                </Td>
                <Td align="end">{money(row.accountBalanceMd)}</Td>
                <Td align="end">{money(row.electronicMd)}</Td>
                <Td align="end">{money(rowTotalMd(row))}</Td>
                <Td align="end">{row.count}</Td>
              </tr>
            ))}
            <tr className="border-t border-strong bg-raised font-semibold">
              <Td>{t('common.total')}</Td>
              <Td align="end" strong>
                {money(totals.cashMd)}
              </Td>
              <Td align="end">{money(totals.accountBalanceMd)}</Td>
              <Td align="end">{money(totals.electronicMd)}</Td>
              <Td align="end">{money(totals.totalMd)}</Td>
              <Td align="end">{totals.count}</Td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-content-muted">
        {t('reports.paymentsByEmployee.drawerNote')}
      </p>
    </>
  );
}

function Th({ children, align }: { children?: React.ReactNode; align?: 'end' }) {
  return (
    <th
      className={`px-3 py-2 font-medium max-md:px-2 ${align === 'end' ? 'text-end' : 'text-start'}`}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  strong,
}: {
  children?: React.ReactNode;
  align?: 'end';
  strong?: boolean;
}) {
  return (
    <td
      className={[
        'px-3 py-2 max-md:px-2',
        align === 'end' ? 'tabular text-end' : 'text-start',
        strong ? 'font-medium text-content' : 'text-content-secondary',
      ].join(' ')}
    >
      {children}
    </td>
  );
}
